import { Warning20Regular } from "@fluentui/react-icons";
import {
  getAccessLLMModels,
  getMessagesOfConversation,
  trackConversationClick,
} from "api";
import { ConstantValues } from "common/constants";
import { useResizer } from "hooks/useResizer";
import { LLMType } from "models/enums/LLMType";
import { IAccessLLMModel } from "models/IAccessLLMModel";
import { IConversation } from "models/IConversation";
import { IWorkspace } from "models/IWorkspace";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { RootState } from "store";
import { isNullUndefinedOrEmpty } from "utils/generalUtils";
import { setAIModels } from "../../redux/accessLLMModelsSlice";
import { setActiveWorkspace } from "../../redux/activeWorkspaceSlice";
import { setCurrentConversation } from "../../redux/conversations";
import { setNewConfigForKey } from "../../redux/llmConfig";
import { setCurrentMessages } from "../../redux/messages";
import { clearReferencedConversations } from "../../redux/referencedConversationsSlice";
import { setCurrentPrompt } from "../../redux/selectedPrompt";
import { setTagsForKey } from "../../redux/tags";
import { setTemporaryChatOpen } from "../../redux/temporaryChat";
import { setLibraryDocumentsForKey } from "../../redux/workspaceLibraryDocuments";
import { useSharedBannerStyles } from "theme/sharedStyles";
import { Chat } from "./Chat";
import { useChatContentPageStyles } from "./ChatContentPage.styles";
import { TemporaryChat } from "./temporaryChat/TemporaryChat";

export const ChatContentPage = (): JSX.Element => {
  const styles = useChatContentPageStyles();
  const bannerStyles = useSharedBannerStyles();
  const navigate = useNavigate();
  const isMobile = useResizer();
  const temporaryChatIsOpen: boolean = useSelector(
    (state: RootState) => state.temporaryChatSlice.isOpen,
  );
  const workspaces: IWorkspace[] = useSelector(
    (state: RootState) => state.workspacesSlice.value,
  );
  const currentUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const activeWorkspace = useSelector(
    (state: RootState) => state.activeWorkspaceSlice.value,
  );
  const [searchParams] = useSearchParams();
  const urlWorkspaceId = searchParams.get("workspaceId");
  const urlConversationId = searchParams.get("conversationId");
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const conversationLoadedRef = useRef<boolean>(false);
  const processedUrlRef = useRef<string | null>(null);
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  const dispatch = useDispatch();

  const loadConversationMessages = useCallback(
    async (conversationId: string) => {
      const conversationMessages = await getMessagesOfConversation({
        conversationId,
      });

      if (
        !Array.isArray(conversationMessages) ||
        conversationMessages.length === 0
      )
        return;

      const lastMessage = conversationMessages[conversationMessages.length - 1];
      const lastMessageSourceRagCategories =
        lastMessage?.llmConfig?.sourceRagCategories;

      dispatch(
        setNewConfigForKey({
          key: ConstantValues.CHAT,
          config: {
            llmType: lastMessage?.llmConfig?.llmType ?? LLMType.CLAUDE,
            citate: false,
            sourceRagCategories: lastMessageSourceRagCategories,
            rag: isNullUndefinedOrEmpty(lastMessageSourceRagCategories)
              ? false
              : lastMessageSourceRagCategories.length > 0,
          },
        }),
      );

      dispatch(setCurrentMessages(conversationMessages));

      dispatch(
        setTagsForKey({
          key: conversationId,
          tags: lastMessage?.tags || [],
        }),
      );

      dispatch(
        setLibraryDocumentsForKey({
          key: conversationId,
          documents: lastMessage?.selectedLibraryDocuments || [],
        }),
      );

      localStorage.setItem(
        ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID,
        conversationId,
      );
    },
    [dispatch],
  );

  useEffect((): void => {
    if (urlConversationId && urlConversationId !== processedUrlRef.current) {
      conversationLoadedRef.current = false;
      setAccessDenied(false);
    }
  }, [urlConversationId]);

  useEffect((): void => {
    if (conversationLoadedRef.current) return;

    const isNavigatingFromMyChats: boolean = !!urlConversationId;

    const targetConversationId: string | null =
      urlConversationId ||
      localStorage.getItem(ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID);
    if (!targetConversationId) return;

    if (isNavigatingFromMyChats && (!urlWorkspaceId || workspaces.length === 0))
      return;

    if (!isNavigatingFromMyChats && !activeWorkspace?.id) return;

    if (urlWorkspaceId) {
      const targetWorkspace: IWorkspace | undefined = workspaces.find(
        (w: IWorkspace) => w.id === urlWorkspaceId,
      );
      const isTeamMember: boolean =
        targetWorkspace?.teamMembers?.some(
          (member) => member.userId === currentUser.id,
        ) ?? false;
      const isOwner: boolean =
        targetWorkspace?.owners.some(
          (owner) => owner.userId === currentUser.id,
        ) ?? false;

      if (!targetWorkspace || (!isTeamMember && !isOwner)) {
        setAccessDenied(true);
        return;
      }

      dispatch(setActiveWorkspace(targetWorkspace));
      localStorage.setItem(
        ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
        urlWorkspaceId,
      );
    }

    conversationLoadedRef.current = true;
    processedUrlRef.current = urlConversationId;
    localStorage.setItem(
      ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID,
      targetConversationId,
    );

    const navigatedConversation: IConversation | undefined =
      location.state?.conversation;
    dispatch(
      setCurrentConversation(
        navigatedConversation ?? {
          id: targetConversationId,
          workspaceId: urlWorkspaceId,
          userId: currentUser.id,
        },
      ),
    );
    dispatch(setTemporaryChatOpen(false));
    dispatch(setCurrentMessages([]));
    dispatch(setCurrentPrompt({ key: ConstantValues.CHAT, prompt: null }));
    dispatch(clearReferencedConversations());

    (async () => {
      try {
        await loadConversationMessages(targetConversationId);

        if (isNavigatingFromMyChats) {
          trackConversationClick({
            conversationId: targetConversationId,
          }).catch(console.error);

          navigate("/", { replace: true });
        }
      } catch (error) {
        console.error("Failed to load conversation:", error);
        if (isNavigatingFromMyChats) {
          setAccessDenied(true);
        } else {
          dispatch(setCurrentConversation(null));
          dispatch(setCurrentMessages([]));
        }
      }
    })();
  }, [
    workspaces,
    activeWorkspace?.id,
    dispatch,
    currentUser.id,
    location.state?.conversation,
    loadConversationMessages,
    urlWorkspaceId,
    urlConversationId,
    navigate,
  ]);

  useEffect((): void => {
    if (accessDenied) {
      backButtonRef.current?.focus();
    }
  }, [accessDenied]);

  useEffect((): void => {
    const fetchAILLMModels = async () => {
      const data: IAccessLLMModel[] = await getAccessLLMModels();
      dispatch(setAIModels(data));
    };

    fetchAILLMModels();
  }, [dispatch]);

  return (
    <div className={styles.container}>
      {accessDenied ? (
        <div className={styles.errorMessage}>
          <div className={bannerStyles.warningBanner} role="alert">
            <Warning20Regular aria-hidden="true" />
            <span className={bannerStyles.warningBannerText}>
              Chat not found or you no longer have access to this workspace.
            </span>
            <button
              ref={backButtonRef}
              className={bannerStyles.warningBannerAction}
              onClick={() => {
                localStorage.removeItem(
                  ConstantValues.DRAFT_MESSAGE_CONVERSATION_ID,
                );
                localStorage.removeItem(
                  ConstantValues.DRAFT_MESSAGE_WORKSPACE_ID,
                );
                localStorage.removeItem(
                  ConstantValues.MY_CHATS_FILTER_WORKSPACE_ID,
                );
                navigate("/chats");
              }}
            >
              Back to My Chats
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.innerContainer}>
            <div className={styles.chatWrapper}>
              <Chat />
            </div>
            {temporaryChatIsOpen && (
              <div
                className={
                  isMobile
                    ? styles.temporaryChatWrapperMobile
                    : styles.temporaryChatWrapper
                }
              >
                <TemporaryChat />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
